




















































































































import {
  IWorkflowFormFieldSubmissionData,
  IWorkflowFormFieldOptionSubmissionData,
  IWorkflowFormSubmission,
  IWorkflowFormTemplateRevision,
  WorkflowFormFieldTypeEnum,
  IWorkflowFormSectionFieldData,
  IWorkflowFormFieldData,
  ISystemError,
  IWorkflowFormSectionSubmissionData,
  WorkflowFormFieldSubmission,
  WorkflowFormSubmission,
  IWorkflowFormSectionSubmissionFieldErrorData
} from '@/models';

import { cloneDeep, isArray } from 'lodash';
import { PropType } from 'vue';
import { mapActions, mapGetters } from 'vuex';

import authMixin from '@/mixins/auth';
import SnackBarMixin from '@/mixins/snackbar';

import {
  diffDeep,
  downloadDataUrl,
  isValidDataUrl,
  setErrorFromAxiosError
} from '@/utils/helpers';
import WorkflowFormSubmissionFields from './WorkflowFormSubmissionFields.vue';
import ErrorAlert from '@/components/common/ErrorAlert.vue';

import mixins from '@/utils/mixins';

const baseMixins = mixins(authMixin, SnackBarMixin);

export default baseMixins.extend({
  name: 'WorkflowSubmissionDetail',

  components: {
    ErrorAlert,
    WorkflowFormSubmissionFields
  },

  props: {
    submission: {
      type: Object as PropType<IWorkflowFormSubmission>
    },
    template: Object as PropType<IWorkflowFormTemplateRevision>,
    canEdit: Boolean
  },

  data() {
    return {
      WorkflowFormFieldTypeEnum,
      error: null as null | ISystemError,
      isSubmissionFormValid: false,

      isEditable: false,
      isLoading: false,
      sectionAnswers: [] as IWorkflowFormSectionSubmissionData[],
      fieldErrors: [] as IWorkflowFormSectionSubmissionFieldErrorData[]
    };
  },

  computed: {
    ...mapGetters({
      getWorkflowFormSubmissionById:
        'workflowForm/getWorkflowFormSubmissionById'
    })
  },

  watch: {
    isEditable(value: boolean) {
      // reset section answers to submission when not editing
      if (!value) {
        this.sectionAnswers = this.genSubmissionAnswers(
          cloneDeep(this.submission)
        );
      }
    },
    submission: {
      handler(value: IWorkflowFormSubmission) {
        if (value) {
          this.sectionAnswers = this.genSubmissionAnswers(cloneDeep(value));
        }
      },
      immediate: true
    }
  },

  methods: {
    isArray,
    isValidDataUrl,
    downloadDataUrl,

    ...mapActions({
      updateWorkflowFormSubmission: 'workflowForm/updateWorkflowFormSubmission'
    }),

    clearAllErrors() {
      this.error = null;
      this.fieldErrors = [];
    },

    addTemplateRow(
      rows: IWorkflowFormFieldSubmissionData[],
      sectionIndex: number
    ) {
      rows.forEach(row => {
        this.sectionAnswers[sectionIndex].fields.push(row);
      });
    },

    genSubmissionAnswers(submission: IWorkflowFormSubmission) {
      const sectionAnswers = [] as IWorkflowFormSectionSubmissionData[];

      this.template.sections?.forEach((section, i) => {
        sectionAnswers.push({
          fields: [] as IWorkflowFormFieldSubmissionData[]
        });

        section.section_fields!.forEach(section_field => {
          let answers = submission.answers.filter(
            answer =>
              (answer.field as IWorkflowFormSectionFieldData).id ==
              section_field.id
          );

          // If no answers exist for the field, create a blank answer
          if (answers.length === 0) {
            answers = [
              new WorkflowFormFieldSubmission({
                value: '',
                field: section_field,
                option_submissions: [] as IWorkflowFormFieldOptionSubmissionData[],
                template_index: 0
              })
            ];
          } else if (
            (section_field.field_template as IWorkflowFormFieldData).type ==
            WorkflowFormFieldTypeEnum.Checkbox
          ) {
            //TODO: MG- 24-03-2022: Look into why checklist with value object causes bug
            answers = answers.map(answer => {
              answer.option_submissions = (answer.option_submissions as IWorkflowFormFieldOptionSubmissionData[]).map(
                (data: IWorkflowFormFieldOptionSubmissionData) => data.option
              );
              return answer;
            });
          }

          sectionAnswers[i].fields.push(...answers);
        });
      });

      return sectionAnswers;
    },

    updateFormSubmissionData() {
      let answerPayload = [] as IWorkflowFormFieldSubmissionData[];

      const originalFormSubmission: IWorkflowFormSubmission = this.getWorkflowFormSubmissionById(
        this.submission.id!
      );

      this.sectionAnswers.forEach((answer, i) => {
        answerPayload = answerPayload.concat(
          answer.fields.map(submission => {
            //this code fixes the bug where a user clears a v-select
            const nullFilterOptions = (submission.option_submissions as IWorkflowFormFieldOptionSubmissionData[]).filter(
              option => option != null
            );

            const options = (nullFilterOptions as IWorkflowFormFieldOptionSubmissionData[]).map(
              (option: IWorkflowFormFieldOptionSubmissionData | number, i) => {
                if (typeof option == 'number') {
                  const org_submission = originalFormSubmission.answers!.find(
                    org =>
                      (org.field as IWorkflowFormSectionFieldData).id ==
                      (submission.field as IWorkflowFormSectionFieldData).id
                  );

                  if (org_submission) {
                    const org_option = (org_submission.option_submissions as IWorkflowFormFieldOptionSubmissionData[]).find(
                      org_option => org_option.option == option
                    );

                    if (org_option) {
                      return org_option;
                    }
                  }

                  return { option: option };
                }
                return option;
              }
            );

            return { ...submission, option_submissions: options };
          })
        );
      });

      //when updating form the id of the created answer fields form will need to be
      //correctly mapped to the answer payload in order to prevent a unique constraint failure
      //of the server
      answerPayload = answerPayload.map((submission, i) => {
        const originalSubmissionAnswer = originalFormSubmission.answers!.find(
          answer =>
            (answer.field as IWorkflowFormSectionFieldData).id ==
            submission.field
        );

        if (!originalSubmissionAnswer) {
          return submission;
        }

        return { ...submission, id: originalSubmissionAnswer.id };
      });

      answerPayload = answerPayload.map(submission => {
        if (typeof submission.field !== 'number') {
          submission.field = submission.field.id!;
        }

        return submission;
      });

      return new WorkflowFormSubmission({
        ...this.submission,
        answers: answerPayload
      });
    },

    async saveWorkflowFormSubmission() {
      const formSubmissionPayload = this.updateFormSubmissionData();
      const originalFormSubmission = this.getWorkflowFormSubmissionById(
        formSubmissionPayload.id!
      );

      const formSubmissionDelta = diffDeep(
        formSubmissionPayload,
        originalFormSubmission,
        true
      );

      await this.updateWorkflowFormSubmission(formSubmissionDelta)
        .then(() => {
          this.$emit('saved');

          this.isEditable = false;

          this.createSnack({
            color: 'success darken-1',
            type: 'success',
            right: true,
            useBtn: false,
            msg: 'Workflow submission updated successfully',
            btnText: 'Ok',
            timeout: 6000
          });
        })
        .catch(error => {
          this.error = setErrorFromAxiosError(error);

          if (error.response) {
            // Form Model is dynamic and the server returns a different model format for its error
            // therefore I have to map the error data to its correct field.
            if (error.response.data.answers) {
              const submissionFieldErrors = [] as IWorkflowFormSectionSubmissionFieldErrorData[];

              let index = 0;

              this.template.sections.forEach((section, n) => {
                submissionFieldErrors.push({ errors: [] });

                section.section_fields?.forEach(field => {
                  submissionFieldErrors[n].errors.push(
                    error.response!.data.answers[index].detail ?? ''
                  );
                  index++;
                });
              });

              this.fieldErrors = submissionFieldErrors;
            }
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  }
});
